import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import type { Schemas } from '@npc/api-client'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { actOnBriefItem, type BriefAction } from './queries'

type Block = Schemas['MessageBlock']

/**
 * 早晨留言里「每一项」那张卡（需求稿 V2 §7.4；服务端 blocks_brief.go 是合同）。
 *
 * 需求稿列了七个动作：安排开发 / 交给本地 AI / 交给平台 AI / 稍后 / 忽略 / 不要再提 / 为什么。
 * 这一版画五个：「为什么」是卡上的 detail 展开，不调服务端；「交给本地 AI」「交给平台 AI」
 * 要把执行者偏好一路带到派活，还没接——不画一颗按不动的按钮。
 *
 * 决定了就把按钮收掉、显示 Lulu 那句话；「安排开发」贴进的是项目话头，卡上给一个链接过去。
 *
 * Soft Structuralism：主 CTA 只用 coral（--lu-accent），禁止绿色主按钮；次级动作为 ghost。
 */
export function isBriefItemCard(b: Block): boolean {
  return b.type === 'brief_item_card'
}

const ACTION_COPY = {
  schedule: 'steward.brief.action.schedule',
  later: 'steward.brief.action.later',
  ignore: 'steward.brief.action.ignore',
  mute: 'steward.brief.action.mute',
} as const

const ACTIONS: readonly BriefAction[] = ['schedule', 'later', 'ignore', 'mute']

export function BriefItemCard({ block, conversationId }: { block: Block; conversationId?: string }) {
  const raw = block as unknown as Record<string, unknown>
  const kind = raw['item_kind']
  const itemId = raw['item_id']
  const title = raw['title']
  const detail = typeof raw['detail'] === 'string' ? raw['detail'] : ''
  const count = typeof raw['count'] === 'number' ? raw['count'] : 0
  const [whyOpen, setWhyOpen] = useState(false)
  const [busy, setBusy] = useState<BriefAction | null>(null)
  const [done, setDone] = useState<{ said: string; conversationId?: string } | null>(null)
  const [failed, setFailed] = useState(false)
  if ((kind !== 'finding' && kind !== 'proposal') || typeof itemId !== 'string' || typeof title !== 'string') {
    return null
  }
  const act = async (action: BriefAction) => {
    if (conversationId === undefined) return
    setBusy(action)
    setFailed(false)
    try {
      const out = await actOnBriefItem({ conversationId, itemKind: kind, itemId, action })
      setDone({ said: out.said, ...(out.conversationId === undefined ? {} : { conversationId: out.conversationId }) })
    } catch {
      setFailed(true)
    } finally {
      setBusy(null)
    }
  }
  return (
    <section
      data-testid="brief-item-card"
      data-item-kind={kind}
      data-item-id={itemId}
      className="lu-brief-card mt-2"
    >
      <div className="lu-brief-meta flex items-center gap-2">
        <span className="lu-brief-kind">
          <T id={kind === 'finding' ? 'steward.brief.item.finding' : 'steward.brief.item.proposal'} />
        </span>
        {count > 1 ? <span data-testid="brief-item-count">{t('steward.brief.item.count', { count: String(count) })}</span> : null}
      </div>
      <p data-testid="brief-item-title" className="lu-brief-title m-0 mt-1.5">
        {title}
      </p>
      {detail !== '' ? (
        <div className="mt-2">
          <button
            type="button"
            data-testid="brief-item-why"
            aria-expanded={whyOpen}
            onClick={() => setWhyOpen((o) => !o)}
            className="lu-brief-ghost"
          >
            <T id="steward.brief.action.why" />
          </button>
          {whyOpen ? (
            <pre
              data-testid="brief-item-detail"
              className="lu-brief-detail mt-1.5 max-h-[240px] overflow-auto whitespace-pre-wrap break-words"
            >
              {detail}
            </pre>
          ) : null}
        </div>
      ) : null}
      {done !== null ? (
        <p data-testid="brief-item-decided" className="lu-brief-decided mb-0 mt-2">
          {done.said}
          {done.conversationId !== undefined && done.conversationId !== conversationId ? (
            <>
              {' '}
              <Link
                data-testid="brief-item-go"
                to="/lulu/c/$conversationId"
                params={{ conversationId: done.conversationId }}
                className="underline"
              >
                <T id="steward.brief.action.go_to_plan" />
              </Link>
            </>
          ) : null}
        </p>
      ) : (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {ACTIONS.map((action) => (
            <button
              key={action}
              type="button"
              data-testid={`brief-item-${action}`}
              data-variant={action === 'schedule' ? 'primary' : 'ghost'}
              disabled={busy !== null || conversationId === undefined}
              onClick={() => void act(action)}
              className={action === 'schedule' ? 'lu-brief-primary' : 'lu-brief-ghost'}
            >
              <T id={ACTION_COPY[action]} />
            </button>
          ))}
          {failed ? (
            <span data-testid="brief-item-failed" className="text-npc-meta text-npc-lulu-bad">
              <T id="error.generic" />
            </span>
          ) : null}
        </div>
      )}
    </section>
  )
}
